/*
 * Copyright (c) 2024 OTB Africa. All rights reserved. <https://otbafrica.com>
 *
 * Created Date: Saturday, July 6th 2024, 5:34:37 pm
 * Author: OTB Africa, developers@otbafrica.com
 *
 * Use of this source code is governed by the license that can be found in
 * the LICENSE file or at https://developers.otbafrica.com/licenses/
 */

import { FieldDependencyCondition, FieldDependencyConditionGroup } from "lib/schemas";

export function getFieldDependencyConditionGroupsAsObject(groups: FieldDependencyConditionGroup[]): {
  [key: string]: FieldDependencyConditionGroup;
} {
  return groups.reduce((prev, curr, index) => ({ ...prev, [curr.id]: curr }), {});
}

type TFieldDependencyConditionForRendering =
  | {
      _isConditionGroup: true;
      key: string;
      fieldDependencyConditionGroup: FieldDependencyConditionGroup;
      groupedConditions: FieldDependencyCondition[];
    }
  | { _isConditionGroup: false; key: string; fieldDependencyCondition: FieldDependencyCondition };

/**
 *
 * This function transforms the list of groups and conditions into a single array that has both invidividual
 * conditions and grouped conditions within a single array. Each object can now either either of two shapes
 *
 * {_isConditionGroup: false, fieldDependencyCondition: FieldDependencyCondition }
 * {_isConditionGroup: tru, fieldDependencyConditionGroup: FieldDependencyConditionGroup, groupedConditions: FieldDependencyCondition[] }
 *
 * @param fieldDependencyConditionGroups the groups for dependency conditions
 * @param fieldDependencyConditions the actual dependency conditions
 */
export function getFieldDependencyConditionsForRendering(
  fieldDependencyConditionGroups: FieldDependencyConditionGroup[],
  fieldDependencyConditions: FieldDependencyCondition[]
): TFieldDependencyConditionForRendering[] {
  const conditionGroupsAsObject = getFieldDependencyConditionGroupsAsObject(fieldDependencyConditionGroups);

  const addedGroupsIndexes = {};

  return fieldDependencyConditions.reduce((accumulatedItems, fieldDependencyCondition) => {
    const itemsForRendering = [...accumulatedItems];

    if (!fieldDependencyCondition.condition_group) {
      itemsForRendering.push({
        _isConditionGroup: false,
        fieldDependencyCondition,
        key: fieldDependencyCondition.artifact_guid,
      });
      return itemsForRendering;
    }

    const addedGroupIndex = addedGroupsIndexes[fieldDependencyCondition.condition_group];
    const addedGroupReference = itemsForRendering[addedGroupIndex];
    if (addedGroupReference?._isConditionGroup === true) {
      addedGroupReference.groupedConditions.push(fieldDependencyCondition);
      return itemsForRendering;
    }

    const fieldDependencyConditionGroup = conditionGroupsAsObject[fieldDependencyCondition.condition_group];
    itemsForRendering.push({
      _isConditionGroup: true,
      fieldDependencyConditionGroup,
      groupedConditions: [fieldDependencyCondition],
      key: fieldDependencyConditionGroup.artifact_guid,
    });
    addedGroupsIndexes[fieldDependencyCondition.condition_group] = itemsForRendering.length - 1;
    return itemsForRendering;
  }, [] as TFieldDependencyConditionForRendering[]);
}
